import SelectComponent, { components } from 'react-select';

import type { StylesConfig, ClearIndicatorProps, DropdownIndicatorProps, MultiValueRemoveProps } from 'react-select';

import DropdownIcon from '@assets/Icons/ChevronDown';
import ClearIcon from '@assets/Icons/XSm';

import styles from './styles.module.scss';

export type SelectValue = {
  label: string;
  value: string;
};

export interface ISelect {
  options: SelectValue[];
  error?: string;
  name: string;
  value?: string | string[];
  onChange: (newValue: string | string[] | undefined) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  label?: string;
  placeholder?: string;
  isMulti?: boolean;
  hideSelectAll?: boolean;
  isDisabled?: boolean;
}

// eslint-disable-next-line max-lines-per-function
const Select = ({ isMulti = false, hideSelectAll, isDisabled, options, error, name, value, onChange, onBlur, label, placeholder }: ISelect) => {
  const generateStyle = (error?: boolean): StylesConfig => {
    return {
      control: (base, { isFocused, isMulti, isDisabled, hasValue }) => ({
        ...base,
        gap: '10px',
        borderWidth: 0,
        boxShadow: isFocused
          ? 'inset 0 0 0 2px var(--fieldBorder--active)'
          : error
          ? 'inset 0 0 0 2px var(--fieldBorder--error)'
          : isDisabled
          ? 'none'
          : 'inset 0 0 0 1px var(--fieldBorder)',
        '&:hover': {
          boxShadow: !isFocused ? 'inset 0 0 0 1px var(--fieldBorder--hover)' : undefined,
        },
        padding: isMulti && hasValue ? '8px 12px 8px 8px' : '12px 12px',
        backgroundColor: isDisabled ? 'var(--bg--secondary)' : 'var(--bg)',
        transition: 'none',
      }),
      valueContainer: (base) => ({
        ...base,
        gap: '4px',
        padding: 0,
      }),
      multiValue: (base) => ({
        ...base,
        alignItems: 'center',
        gap: '4px',
        margin: 0,
        borderRadius: '4px',
        padding: '6px 8px',
        backgroundColor: 'var(--bg--secondary)',
        font: '500 14px/20px "ARS Maquette Pro", "Helvetica Neue", Arial, sans-serif',
      }),
      multiValueLabel: (base) => ({
        ...base,
        padding: 0,
        paddingLeft: 0,
        fontSize: '14px',
        color: 'var(--text--secondary)',
      }),
      multiValueRemove: (base) => ({
        ...base,
        padding: 0,
        backgroundColor: 'transparent',
        color: 'var(--text--secondary)',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'transparent',
          color: 'var(--text)',
        },
      }),
      input: (base) => ({
        ...base,
        padding: 0,
        margin: 0,
      }),
      placeholder: (base) => ({
        ...base,
        color: 'var(--text--placeholder)',
      }),
      singleValue: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
      }),
      indicatorsContainer: (base) => ({
        ...base,
        alignSelf: 'center',
      }),
      clearIndicator: (base) => ({
        ...base,
        padding: 0,
        color: 'var(--text)',
        '&:hover': {
          color: 'var(--text--secondary)',
        },
        cursor: 'pointer',
      }),
      indicatorSeparator: (base, { hasValue, isMulti }) => ({
        ...base,
        display: isMulti && hasValue ? 'block' : 'none',
        marginTop: 0,
        marginBottom: 0,
        marginLeft: '8px',
        marginRight: '7px',
        backgroundColor: 'var(--fieldBorder)',
      }),
      dropdownIndicator: (base) => ({
        ...base,
        padding: 0,
        color: 'var(--text)',
        '&:hover': {
          color: 'var(--text)',
        },
      }),
      menu: (base) => ({
        ...base,
        margin: 0,
        overflow: 'hidden',
      }),
      menuList: (base) => ({
        ...base,
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'none',
        scrollbarWidth: 'thin',
        scrollbarColor: 'var(--border) var(--bg--secondary)',
        '&::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'var(--bg--secondary)',
        },
        '&::-webkit-scrollbar-corner': {
          background: 'var(--bg--secondary)',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'var(--border)',
          borderRadius: 0,
        },
      }),
      option: (base, { isDisabled, isFocused, isSelected }) => {
        return {
          ...base,
          backgroundColor: isSelected
            ? isFocused
              ? 'var(--actionListBg--active--hover)'
              : 'var(--actionListBg--active)'
            : isFocused
            ? 'var(--actionListBg--hover)'
            : undefined,
          color: isDisabled ? 'var(--actionListText--disabled)' : isSelected ? 'var(--actionListText--active)' : 'var(--text)',
          cursor: isDisabled ? 'not-allowed' : 'default',
        };
      },
    };
  };
  const ClearIndicator = (props: ClearIndicatorProps) => {
    return (
      <components.ClearIndicator {...props}>
        <ClearIcon size={20} />
      </components.ClearIndicator>
    );
  };
  const DropdownIndicator = (props: DropdownIndicatorProps) => {
    return (
      <components.DropdownIndicator {...props}>
        <DropdownIcon size={20} />
      </components.DropdownIndicator>
    );
  };
  const MultiValueRemove = (props: MultiValueRemoveProps) => {
    return (
      <components.MultiValueRemove {...props}>
        <ClearIcon size={16} />
      </components.MultiValueRemove>
    );
  };

  const changeHandler = (newValue: unknown) => {
    if (Array.isArray(newValue)) {
      onChange(newValue.length === 0 ? undefined : newValue.map((option) => option.value));
    } else {
      onChange((newValue as SelectValue)?.value);
    }
  };

  const getValue = () => {
    if (Array.isArray(value)) return value.map((v) => options.find((item) => item.value === v));
    else options.find((item) => item.value === value);
  };

  return (
    <div className={styles.container}>
      {label && (
        <div className={styles.labelContainer}>
          <label className={styles.label}>{label}</label>
          {isMulti && !hideSelectAll && (
            <label onClick={() => changeHandler(options)} className={styles.selectAll}>
              All
            </label>
          )}
        </div>
      )}
      <SelectComponent
        name={name}
        isMulti={isMulti}
        isDisabled={isDisabled}
        placeholder={placeholder}
        options={options}
        noOptionsMessage={() => 'No options'}
        value={getValue()}
        styles={generateStyle(!!error)}
        onChange={changeHandler}
        onBlur={onBlur}
        components={{
          ClearIndicator,
          DropdownIndicator,
          MultiValueRemove,
        }}
      />
      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
};

export default Select;
